import type { IndividualSealCalculations } from "../calculationTypes";
import { computed } from "vue";
import { calculateMleEllipseMaxDepth } from "@/libs/calculations/cables";
import { calculateSealOperatingHP } from "@/libs/calculations/pumps";
import { getFrequencyRatio } from "@/libs/calculations/pumps/pumpData";
import {
  getSealBearingData,
  getSealExpansionInformation,
  getShutInSealThrustData
} from "@/libs/calculations/seals";
import type { SealsCtx } from "./context";

// Seal bearing data, expansion and thrust calcs.
export const defineSeals = (ctx: SealsCtx) => {
  const {
    _equipment,
    isScenarioEspShore,
    descendingOrderSeals,
    primaryPump,
    primarySeal,
    bottomSeal,
    bottomOfIntakesDepthFt,
    bottomOfTopSealDepthFt,
    bottomOfSealsDepthFt,
    bottomholeTemperatureF,
    surfaceTemperatureF,
    basicData,
    operatingThrustLoad,
    totalRequiredBrakeHP,
    shutInData,
    toleranceData,
    operatingRPM,
    motorInternalTemperatureF,
    motorSkinTemperatureF
  } = ctx;

  // These are the real bearing data objects for seals
  // since they are using the operatingRPM coming from pumpData
  const primarySealBearingData = computed(() =>
    getSealBearingData(
      primarySeal.value?.r_seal,
      operatingRPM.value,
      motorSkinTemperatureF.value,
      _equipment.value?.r_seal_motor_oil
    )
  );

  const bottomSealBearingData = computed(() =>
    getSealBearingData(
      bottomSeal.value?.r_seal,
      operatingRPM.value,
      motorSkinTemperatureF.value,
      _equipment.value?.r_seal_motor_oil
    )
  );

  // This is also used to calculated shut in data
  // If there is just a primary seal then it will be lower tandem
  // If there are 2 seals it will run both lower and upper tandem calcs
  const sealOperatingInfo = computed(() =>
    getSealExpansionInformation(
      primarySeal.value?.r_seal,
      bottomSeal.value?.r_seal,
      bottomholeTemperatureF.value,
      surfaceTemperatureF.value,
      _equipment.value?.r_motor?.oil_volume_gallons,
      motorInternalTemperatureF.value,
      motorSkinTemperatureF.value,
      basicData.value.datumPointFt,
      _equipment.value?.r_seal_motor_oil,
      bottomOfSealsDepthFt.value,
      bottomOfTopSealDepthFt.value,
      bottomOfIntakesDepthFt.value
    )
  );

  const individualSealCalculations = computed<IndividualSealCalculations>(() =>
    descendingOrderSeals.value.reduce<IndividualSealCalculations>(
      (acc, xrefSeal) => {
        const isPrimarySeal = xrefSeal.id === primarySeal.value?.id;
        const frequencyRatio = getFrequencyRatio(
          operatingRPM.value,
          isScenarioEspShore.value
        );

        const ellipseAngle = Math.atan(
          _equipment.value?.r_mle?.width_inches /
            2 /
            (xrefSeal?.r_seal?.outer_diameter_inches +
              _equipment.value?.r_mle?.depth_inches)
        );
        const mleMaxEllipseDepthIn = calculateMleEllipseMaxDepth(
          ellipseAngle,
          _equipment.value?.r_mle?.depth_inches,
          xrefSeal?.r_seal?.outer_diameter_inches,
          0,
          false
        );
        const maxCalculatedOuterDiameterIn =
          mleMaxEllipseDepthIn + xrefSeal?.r_seal?.outer_diameter_inches;

        const bearingRatingAtRPM = isPrimarySeal
          ? primarySealBearingData.value.bearingRatingAtRPMLBS
          : bottomSealBearingData.value.bearingRatingAtRPMLBS;

        const viscosityCp = isPrimarySeal
          ? primarySealBearingData.value.viscosityCp
          : bottomSealBearingData.value.viscosityCp;

        // This is the real seal operating HP, different from the required
        // one that comes from pumpData
        const operatingThrustLoadForHP = isPrimarySeal
          ? operatingThrustLoad.value
          : xrefSeal?.r_seal?.weight_pounds;

        const operatingHP = calculateSealOperatingHP(
          xrefSeal?.r_seal?.thrust_collar_outer_diameter_inches || 0,
          xrefSeal?.r_seal?.thrust_collar_inner_diameter_inches || 0,
          bearingRatingAtRPM || 0,
          viscosityCp,
          operatingThrustLoadForHP,
          frequencyRatio,
          isScenarioEspShore.value
        );
        const bearingLoadRatio =
          operatingThrustLoadForHP / bearingRatingAtRPM || 0;

        // User lower tandem expansion calcs by default
        let useLowerTandemExpansionCalcs = true;
        // If more than 1 seal and it's the primary seal, use upper tandem
        if (descendingOrderSeals.value.length > 1 && isPrimarySeal) {
          useLowerTandemExpansionCalcs = false;
        }

        acc[xrefSeal.id] = {
          xrefSeal,
          mleMaxEllipseDepthIn,
          maxCalculatedOuterDiameterIn,
          bearingRatingAt60hzLBS: isPrimarySeal
            ? primarySealBearingData.value.bearingRatingAt60hzLBS
            : bottomSealBearingData.value.bearingRatingAt60hzLBS,
          bearingRatingAtRPMLBS: bearingRatingAtRPM,
          bearingLoadRatio,
          shutInBearingLoadRatio: isPrimarySeal
            ? shutInData.value.seal.shutInBearingLoadRatio
            : 0,
          shutInThrustLoadPounds:
            isPrimarySeal &&
            primaryPump.value?.r_pump?.shaft_diameter_inches &&
            shutInData.value.dischargeTotalPressurePSIA
              ? getShutInSealThrustData(
                  shutInData.value.dischargeTotalPressurePSIA,
                  primaryPump.value.r_pump.shaft_diameter_inches
                )
              : 0,
          operatingHP,
          shaftPowerLimitStandardHP:
            toleranceData.value.seals[xrefSeal.id].shaftPowerLimitStandardHP,
          shaftPowerLimitHighStrengthHP:
            toleranceData.value.seals[xrefSeal.id]
              .shaftPowerLimitHighStrengthHP,
          standardShaftRatingRatio:
            totalRequiredBrakeHP.value /
            toleranceData.value.seals[xrefSeal.id].shaftPowerLimitStandardHP,
          highStrengthShaftRatingRatio:
            totalRequiredBrakeHP.value /
            toleranceData.value.seals[xrefSeal.id]
              .shaftPowerLimitHighStrengthHP,
          // operation Amount of Expansion for each seal
          operationalTotalAmountOfExpansionGal: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.operation
                ?.totalAmountOfExpansionGal || 0
            : sealOperatingInfo.value.upperTandem.operation
                ?.totalAmountOfExpansionGal || 0,
          // operation volume for expansion for each seal
          operationalTotalExpansionGal: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.operation
                .totalSealExpansionGal || 0
            : sealOperatingInfo.value.upperTandem.operation
                .totalSealExpansionGal || 0,
          // pull Amount of Expansion for each seal
          pullTotalAmountOfExpansionGal: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.pull
                ?.totalAmountOfExpansionGal || 0
            : sealOperatingInfo.value.upperTandem.pull
                ?.totalAmountOfExpansionGal || 0,
          // pull volume for expansion for each seal
          pullTotalExpansionGal: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.pull.totalSealExpansionGal ||
              0
            : sealOperatingInfo.value.upperTandem.pull.totalSealExpansionGal ||
              0,
          // total volume for contraction
          totalContractionVolumeExpansionGal: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.totalContractionVolumeGal
            : sealOperatingInfo.value.upperTandem.totalContractionVolumeGal,
          // install expansion ratio for each seal
          installExpansionRatio: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.install.expansionRatio || 0
            : sealOperatingInfo.value.upperTandem.install.expansionRatio || 0,
          // operation expansion ratio for each seal
          operationExpansionRatio: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.operation.expansionRatio || 0
            : sealOperatingInfo.value.upperTandem.operation.expansionRatio || 0,
          // pull expansion ratio for each seal
          pullExpansionRatio: useLowerTandemExpansionCalcs
            ? sealOperatingInfo.value.lowerTandem.pull.expansionRatio || 0
            : sealOperatingInfo.value.upperTandem.pull.expansionRatio || 0
        };
        return acc;
      },
      {} as IndividualSealCalculations
    )
  );

  const sealOperatingHP = computed(
    () =>
      individualSealCalculations.value[primarySeal?.value?.id]?.operatingHP +
      (individualSealCalculations.value[bottomSeal?.value?.id]?.operatingHP ||
        0)
  );

  const sealTotalVolumeGallons = computed(() => {
    return (
      primarySeal.value?.r_seal?.total_seal_volume_gallons +
      (bottomSeal.value?.r_seal?.total_seal_volume_gallons || 0)
    );
  });

  const sealUpperChamberPullExpansionRatio = computed(
    () =>
      sealOperatingInfo.value.lowerTandem.pull.upperChamberPullExpansionRatio ||
      0
  );

  const sealBearingLoadRatio = computed(
    () =>
      (individualSealCalculations.value[primarySeal?.value?.id]
        ?.bearingLoadRatio || 0) +
      (individualSealCalculations.value[bottomSeal?.value?.id]
        ?.bearingLoadRatio || 0)
  );

  const sealShutInBearingLoadRatio = computed(
    () =>
      (individualSealCalculations.value[primarySeal?.value?.id]
        ?.shutInBearingLoadRatio || 0) +
      (individualSealCalculations.value[bottomSeal?.value?.id]
        ?.shutInBearingLoadRatio || 0)
  );

  const sealBearingRatingAt60HzLBS = computed(
    () =>
      (individualSealCalculations.value[primarySeal?.value?.id]
        ?.bearingRatingAt60hzLBS || 0) +
      (individualSealCalculations.value[bottomSeal?.value?.id]
        ?.bearingRatingAt60hzLBS || 0)
  );

  const sealBearingRatingAtRPMLBS = computed(
    () =>
      (individualSealCalculations.value[primarySeal?.value?.id]
        ?.bearingRatingAtRPMLBS || 0) +
      (individualSealCalculations.value[bottomSeal?.value?.id]
        ?.bearingRatingAtRPMLBS || 0)
  );

  return {
    primarySealBearingData,
    bottomSealBearingData,
    sealOperatingInfo,
    individualSealCalculations,
    sealOperatingHP,
    sealTotalVolumeGallons,
    sealUpperChamberPullExpansionRatio,
    sealBearingLoadRatio,
    sealShutInBearingLoadRatio,
    sealBearingRatingAt60HzLBS,
    sealBearingRatingAtRPMLBS
  };
};

export type SealsCalcs = ReturnType<typeof defineSeals>;
